import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { LogService } from '../../shared/log/log.service';
import { AuthenticationHttpService } from './authentication.http.service';
import { JwtHelper } from 'angular2-jwt';
import { UserLocalStorageService } from './user.local.storage.service';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { UserPermission } from '../models/user-permission.model';
import { isDefined } from '@ngx-translate/core/src/util';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { User } from '../../auth/models/User';


interface DecodedToken {
  email: string,
  firstname: string,
  lastname: string
}

@Injectable()
export class AuthenticationStore {

  public static readonly TOKEN_NAME = 'id_token';
  private static readonly EXTERNAL_LOGIN = 'external_login';
  protected jwtHelper: JwtHelper = new JwtHelper();

  private userAuthenticated: ReplaySubject<boolean> = new ReplaySubject(1);
  private userBehaviorSubject: BehaviorSubject<User> = new BehaviorSubject(new User());

  constructor(
    private authenticationHttpService: AuthenticationHttpService,
    private logService: LogService,
    private userLocalStorageService: UserLocalStorageService
  ) {
    this.init();
  }

  protected init(): void {
    const encodedToken = this.getToken();
    this.setUserFromToken(encodedToken);
  }

  get user(): Observable<User> {
    return this.userBehaviorSubject.asObservable();
  }

  login(username: string, password: string): Observable<boolean> {
    const login: Observable<UserPermission> = this.authenticationHttpService.login(username, password);

    return login.map((userPermission: UserPermission) => {
        if (userPermission && userPermission.token) {
          // set token property
          const tokenString = userPermission.token;

          this.logService.debug(`expiration: ${this.jwtHelper.getTokenExpirationDate(tokenString)}`);
          this.logService.debug(`is expired: ${this.jwtHelper.isTokenExpired(tokenString)}`);
          this.logService.debug(`decoded: ${JSON.stringify(this.jwtHelper.decodeToken(tokenString))}`);

          // store username and jwt token in local storage to keep user logged in between page refreshes
          this.userLocalStorageService.setUserToken(userPermission);
          this.setExternalLogin(false);
          this.updateState();
          this.setUserFromToken(userPermission.token);
          // return true to indicate successful login
          return true;
        } else {
          this.userLocalStorageService.removeUserToken();

          // return false to indicate failed login
          return false;
        }
      }
    );
  }

  handleTokenFromExternalSource(token: string): Observable<UserPermission> {
    try {
      // Make sure token is in the right format.  This will throw an error if it's not.
      this.jwtHelper.decodeToken(token);
    } catch (error) {
      this.logService.error(`Invalid token format [${token}] with error: ${error}`);
      return Observable.throw(ExternalSourceError.TOKEN_INVALID_FORMAT);
    }

    // Make sure token is not expired
    if (this.jwtHelper.isTokenExpired(token)) {
      this.logService.error(`Token is expired: ${token}`);
      return Observable.throw(ExternalSourceError.TOKEN_EXPIRED);
    }

    return this.authenticationHttpService.getPermissions(token)
      .catch(error => {
        this.logService.error(`Failed to get permissions`);
        return Observable.throw(ExternalSourceError.DEFAULT_ERROR);
      })
      .map(userPermission => {
        this.logService.debug('Returned user permissions');

        // Make sure we got a valid response back from the server
        if (userPermission && userPermission.token) {
          // store username and jwt token in local storage to keep user logged in between page refreshes
          this.userLocalStorageService.setUserToken(userPermission);
          this.setExternalLogin(true);
          this.setUserFromToken(userPermission.token);
          return userPermission;
        } else {
          this.logService.error(`Received invalid permissions`);
          throw new Error('Invalid UserPermission');
        }
      });
  }

  getToken(): string {
    return this.userLocalStorageService.getToken();
  }

  removeToken(): void {
    this.userLocalStorageService.removeItem(AuthenticationStore.TOKEN_NAME);
  }

  isExternalLogin(): boolean {
    const loginSourceStr = this.userLocalStorageService.getItem(AuthenticationStore.EXTERNAL_LOGIN);
    return loginSourceStr === 'true';
  }

  setExternalLogin(externalLogin: boolean): void {
    this.userLocalStorageService.setItem(AuthenticationStore.EXTERNAL_LOGIN, externalLogin ? 'true' : 'false');
  }

  removeExternalLogin(): void {
    this.userLocalStorageService.removeItem(AuthenticationStore.EXTERNAL_LOGIN);
  }

  hasPermission(testPermission: string): boolean;
  hasPermission(testPermissions: string[]): boolean;
  hasPermission(arg: string | string[]): boolean {
    let testPermissions: string[];
    if (arg instanceof Array) {
      // arg must be string[]
      testPermissions = arg.map(perm => perm.toLowerCase());
    } else {
      // arg must be a string
      testPermissions = [ arg.toLowerCase() ];
    }

    const userPermissions: string[] = this.userLocalStorageService.getPermissions().map(perm => perm.toLowerCase());

    if (userPermissions.find((up: string) => isDefined(testPermissions.find(tp => tp === up)))) {
      return true;
    } else {
      return false;
    }
  }

  private isTokenExpired(): boolean {
    const token = this.userLocalStorageService.getToken();

    if (!token) {
      return true;
    }

    return this.jwtHelper.isTokenExpired(token);
  }

  private isTokenValid(): boolean {
    const isTokenValid = !this.isTokenExpired();

    this.logService.verbose(`isTokenValid: ${isTokenValid}`);
    return isTokenValid;
  }

  /**
   * This method will test to determine if the current token is expired.  It will put those
   * results in the Subject so that listeners will be made aware.
   *
   */
  private updateState() {
    this.userAuthenticated.next(this.isTokenValid());
  }

  isLoggedIn(): Observable<boolean> {
    this.updateState();
    return this.userAuthenticated.asObservable();
  }

  get permissions(): string[] {
    return this.userLocalStorageService.getPermissions();
  }

  logout() {
    this.removeToken();
    this.removeExternalLogin();
    this.updateState();
  }

  private setUserFromToken(token: string): void {
    if (!token) {
      return;
    }
    const decodedToken: DecodedToken = this.jwtHelper.decodeToken(token);
    const user = new User({
      email: decodedToken.email || '',
      firstName: decodedToken.firstname || '',
      lastName: decodedToken.lastname || ''
    });
    this.setUserBehaviorSubject(user);
  }

  // marked protected so that MockAuthenticationStore can override as public
  protected setUserBehaviorSubject(user: User) {
    this.userBehaviorSubject.next(user);
  }

}

export enum ExternalSourceError {
  TOKEN_EXPIRED,
  TOKEN_INVALID_FORMAT,
  DEFAULT_ERROR
}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/UAT-GroupPay/workspace/pioneer-web/src/app/permission/services/authentication.store.ts